import { api } from './client';

export const PROJECT_COLOR_OPTIONS = [
  { name: 'Red', hex: '#EF4444' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Amber', hex: '#F59E0B' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Lime', hex: '#84CC16' },
  { name: 'Green', hex: '#22C55E' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Cyan', hex: '#06B6D4' },
  { name: 'Sky', hex: '#0EA5E9' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Violet', hex: '#8B5CF6' },
  { name: 'Purple', hex: '#A855F7' },
  { name: 'Fuchsia', hex: '#D946EF' },
  { name: 'Pink', hex: '#EC4899' },
] as const;

export interface ProjectColor {
  id: string;
  name: string;
  color: string;
  colorHex: string;
  description: string;
  tabCount: number;
  tabIds: string[];
  createdAt: string;
  updatedAt: string;
  isDefault?: boolean;
  isShared?: boolean;
  collaboratorCount?: number;
  ownerName?: string;
}

interface ApiProject {
  id: string;
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  tab_count?: number;
  tab_ids?: string[];
  is_active?: boolean;
  is_default?: boolean;
  is_shared?: boolean;
  collaborator_count?: number;
  owner_name?: string;
  created_at?: string;
  updated_at?: string;
}

const DEFAULT_HEX = '#3B82F6';

export function normalizeProjectHex(color?: string | null): string {
  if (!color) return DEFAULT_HEX;
  const trimmed = color.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  const byName = PROJECT_COLOR_OPTIONS.find(
    (c) => c.name.toLowerCase() === trimmed.toLowerCase()
  );
  if (byName) return byName.hex;
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return DEFAULT_HEX;
}

export function colorNameFromHex(hex: string): string {
  const normalized = normalizeProjectHex(hex);
  return (
    PROJECT_COLOR_OPTIONS.find(
      (c) => c.hex.toUpperCase() === normalized.toUpperCase()
    )?.name ?? 'Custom'
  );
}

export function hexWithAlpha(hex: string, alphaHex = '20'): string {
  return `${normalizeProjectHex(hex)}${alphaHex}`;
}

export function mapApiProject(raw: ApiProject): ProjectColor {
  const colorHex = normalizeProjectHex(raw.color);
  return {
    id: raw.id,
    name: raw.name?.trim() || 'Untitled',
    color: colorNameFromHex(colorHex),
    colorHex,
    description: raw.description ?? '',
    tabCount: raw.tab_count ?? raw.tab_ids?.length ?? 0,
    tabIds: raw.tab_ids ?? [],
    createdAt: raw.created_at ?? '',
    updatedAt: raw.updated_at ?? '',
    isDefault: raw.is_default ?? false,
    isShared: raw.is_shared ?? false,
    collaboratorCount: raw.collaborator_count,
    ownerName: raw.owner_name,
  };
}

function buildPayload(data: {
  name?: string;
  description?: string;
  colorHex?: string;
  color?: string;
}) {
  return {
    name: data.name,
    description: data.description,
    color: normalizeProjectHex(data.colorHex ?? data.color),
  };
}

export const projectColorsService = {
  async list(): Promise<ProjectColor[]> {
    const [ownedRes, sharedRes] = await Promise.all([
      api.get<{ projects: ApiProject[] }>('/projects'),
      api.get<{ projects: ApiProject[] }>('/projects/shared-with-me').catch(() => ({
        data: { projects: [] as ApiProject[] },
      })),
    ]);
    const merged = new Map<string, ProjectColor>();
    for (const raw of ownedRes.data.projects ?? []) {
      const p = mapApiProject(raw);
      merged.set(p.id, p);
    }
    for (const raw of sharedRes.data.projects ?? []) {
      const p = mapApiProject({ ...raw, is_shared: true });
      merged.set(p.id, { ...p, isShared: true });
    }
    return Array.from(merged.values());
  },

  async get(id: string): Promise<ProjectColor> {
    const response = await api.get<ApiProject>(`/projects/${id}`);
    return mapApiProject(response.data);
  },

  async create(data: {
    name: string;
    description?: string;
    colorHex?: string;
  }): Promise<ProjectColor> {
    const payload = buildPayload(data);
    const response = await api.post<ApiProject>('/projects', payload);
    return mapApiProject({
      ...response.data,
      name: payload.name,
      description: payload.description,
      color: payload.color,
    });
  },

  async update(
    id: string,
    data: { name?: string; description?: string; colorHex?: string }
  ): Promise<ProjectColor> {
    const payload = buildPayload(data);
    const response = await api.put<ApiProject>(`/projects/${id}`, payload);
    if (response.data?.id) {
      return mapApiProject(response.data);
    }
    return mapApiProject({
      id,
      name: payload.name,
      description: payload.description,
      color: payload.color,
    });
  },

  async updateColor(id: string, colorHex: string): Promise<string> {
    const color = normalizeProjectHex(colorHex);
    await api.put(`/projects/${id}/color`, { color });
    return color;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  async assignTab(projectId: string, tabId: string): Promise<void> {
    await api.post(`/projects/${projectId}/tabs/${tabId}`);
  },

  async removeTab(projectId: string, tabId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/tabs/${tabId}`);
  },
};
